import type { FeatureCollection, Position } from 'geojson'
import type { ExpressionSpecification } from 'maplibre-gl'

/**
 * Build a MapLibre `within` filter expression that keeps only features whose
 * geometry lies inside the combined area of the given geonet polygons.
 *
 * Why: lanes stream from the PMTiles `segments` road graph, and at far (low)
 * zoom the archive packs the whole metro into a single, coarsely-quantized tile
 * — so the lanes render as a torn mass. MapLibre can't overzoom downward to
 * recover detail, so instead we confine lanes to the (small) geonet areas. This
 * is also the product behaviour we want: lanes only show inside geonet zones and
 * appear/disappear as those zones are toggled.
 *
 * All polygon rings from every collection are merged into one MultiPolygon so a
 * lane crossing a shared border between two adjacent, visible geonets is still
 * contained by the union ("combined on geonet borders"). Returns null when there
 * are no polygons — callers then leave lanes unconfined.
 */
export const buildGeonetWithinExpression = (
  collections: FeatureCollection[],
): ExpressionSpecification | null => {
  const polygons: Position[][][] = []
  for (const fc of collections) {
    for (const feature of fc.features) {
      const geom = feature.geometry
      if (geom.type === 'Polygon') {
        polygons.push(geom.coordinates)
      } else if (geom.type === 'MultiPolygon') {
        for (const poly of geom.coordinates) polygons.push(poly)
      }
    }
  }
  if (polygons.length === 0) return null
  // `within` accepts Polygon/MultiPolygon/Feature/FeatureCollection; the geometry
  // object isn't part of the expression tuple type, hence the cast.
  return [
    'within',
    { type: 'MultiPolygon', coordinates: polygons },
  ] as unknown as ExpressionSpecification
}
